import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const heroProductImage = '/images/products/kylendar-hero-composite-v8.webp';

const integrations = [
  ["/images/integrations/google-calendar.png", "Google Calendar"],
  ["/images/integrations/outlook.png", "Outlook Calendar"],
  ["/images/integrations/apple-calendar.png", "Apple Calendar"],
];

export default function HeroSection() {
  const { t } = useLanguage();
  const copy = t("kylendar.hero");

  return (
    <section id="home-hero" className="home-hero-surface relative overflow-hidden border-b border-brand-100/70">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-brand/60 via-emerald-300/40 to-transparent" aria-hidden="true" />
      <span className="pointer-events-none absolute -left-20 top-8 h-72 w-72 rounded-full bg-brand-200/25 blur-3xl" aria-hidden="true" />
      <span className="pointer-events-none absolute bottom-10 right-0 h-80 w-80 rounded-full bg-emerald-100/40 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-[1664px] px-5 pb-10 pt-10 sm:px-8 lg:static lg:flex lg:min-h-[600px] lg:items-center lg:px-10 lg:pb-12 lg:pt-12 xl:min-h-[640px] 2xl:px-16">
        <div className="relative z-20 w-full max-w-[32.5rem]">
          <p className="mb-3 text-sm font-semibold text-brand-700">{copy.eyebrow}</p>
          <h1 className="home-hero-title font-display w-full text-[2.75rem] font-extrabold leading-[1.02] tracking-[-0.055em] text-slate-950 sm:text-6xl lg:text-[3.65rem]">
            {copy.titleOne}
            <br />
            <span className="text-gradient">{copy.titleTwo}</span>
          </h1>
          <p className="mt-5 max-w-md text-base leading-7 text-slate-600">
            {copy.body}
          </p>
          <div className="mt-8 flex w-[calc(100%_-_60px)] gap-3">
            <a
              href="#conversion"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-700 px-6 py-3.5 text-sm font-bold text-white shadow-[0_14px_32px_-16px_rgba(8,122,88,.7)] transition hover:-translate-y-0.5 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
            >
              {copy.shop} <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/templates"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-brand-200/90 bg-white/90 px-6 py-3.5 text-sm font-bold text-brand-800 transition hover:border-brand hover:bg-white focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
            >
              {copy.templates}
            </Link>
          </div>
          <div className="home-hero-integrations mt-8 flex w-[calc(100%_-_60px)] items-center gap-3 border-t border-brand-100/90 pt-5 text-sm font-medium text-slate-600 sm:gap-4">
            <div className="flex shrink-0 items-center gap-3" aria-label={`${copy.worksWith} ${copy.worksWithSecond}`}>
              {integrations.map(([src, name]) => (
                <img key={name} src={src} alt={name} className="h-10 w-10 rounded-lg object-contain shadow-sm" />
              ))}
            </div>
            <span className="min-w-0 flex-1 text-left leading-6">{copy.worksWith}<br />{copy.worksWithSecond}</span>
          </div>
        </div>

        <div
          className="home-hero-stage relative z-10 mx-auto mt-6 aspect-[4/3] w-full max-w-3xl overflow-hidden lg:absolute lg:inset-y-0 lg:right-0 lg:mt-0 lg:aspect-auto lg:w-[70%] lg:max-w-[1450px] xl:w-[68%]"
          role="img"
          aria-label={t("accessibility.heroProducts")}
        >
          <div
            className="home-hero-blend absolute inset-0"
            style={{ backgroundImage: `url("${heroProductImage}")` }}
            aria-hidden="true"
          />

          <ModelLabel
            className="left-[26%] top-[38%] sm:left-[27%] sm:top-[36%] lg:left-[27%] lg:top-[42%] xl:left-[27%] xl:top-[42%]"
            name="Mini"
            size='4.2"'
          />
          <ModelLabel
            className="left-[62%] top-[22%] sm:left-[63%] sm:top-[20%] lg:left-[68%] lg:top-[31%] xl:left-[68%] xl:top-[31%]"
            name="Standard"
            size='7.5"'
          />
        </div>
      </div>

      <div className="relative z-30 border-t border-brand-100/80 bg-[#f7fffb]/95">
        <div className="mx-auto grid max-w-[1664px] px-5 py-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 sm:grid-cols-3 sm:px-8 lg:px-10 2xl:px-16">
          {copy.facts.map((fact, index) => (
            <span key={fact} className={`flex items-center gap-2 py-1.5 ${index < 2 ? "sm:border-r sm:border-brand-100" : "sm:justify-end"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${index === 0 ? "bg-brand" : index === 1 ? "bg-emerald-400" : "bg-cyan-400"}`} /> {fact}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function ModelLabel({ className, name, size }) {
  return (
    <div className={`home-model-label ${name === "Standard" ? "home-model-label--standard" : "home-model-label--mini"} absolute z-30 inline-flex -translate-x-1/2 flex-col items-center ${className}`}>
      <span className="inline-flex items-center gap-2 rounded-2xl border border-white/80 bg-white/75 px-3 py-2 text-[11px] font-semibold text-slate-800 shadow-[0_18px_34px_-16px_rgba(8,122,88,0.5),0_5px_14px_-8px_rgba(15,23,42,0.3)] ring-1 ring-brand-100/70 backdrop-blur-xl sm:px-3.5 sm:text-xs">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-brand shadow-[0_0_0_3px_rgba(14,181,126,0.16)]" aria-hidden="true" />
          <span className="tracking-[-0.01em]">{name}</span>
        </span>
        <span className="h-3 w-px bg-brand-200" aria-hidden="true" />
        <span className="font-semibold text-brand-700">{size}</span>
      </span>
      <span className="h-4 w-px bg-gradient-to-b from-brand-300/90 to-brand/80 sm:h-5" aria-hidden="true" />
      <span className="h-1.5 w-1.5 rounded-full bg-brand shadow-[0_0_0_3px_rgba(14,181,126,0.18),0_0_14px_rgba(14,181,126,0.38)]" aria-hidden="true" />
    </div>
  );
}
